import QRCode from 'qrcode';
import { bindImage } from './core/image_utils';
import {
  cleanChannelName,
  getAudienceUrl,
  getAudienceDisplayUrl,
} from './core/channel_urls';
import { copyWithFeedback } from './core/clipboard';
import {
  CLASS_CLOSING,
  CLASS_HIDDEN,
  EVENT_CLICK,
  EVENT_KEYDOWN,
  KEY_ESCAPE,
} from './core/strings';

export interface ChannelBadgeOptions {
  channel: string;
  url?: string;
  showLogo?: boolean;
  customLogoUrl?: string | null;
  clickable?: boolean;
}

/**
 * Opens the static #rtc-qr-modal already present in the HTML.
 * Dynamically updates its title, copy URL input, and QR code SVG.
 */
export function openQRCodeModal(url: string, channelName: string): void {
  const modal = document.getElementById('rtc-qr-modal');
  if (!modal) {
    console.warn('#rtc-qr-modal not found in document');
    return;
  }

  const title = modal.querySelector('#qr-modal-title');
  if (title) {
    title.textContent = getAudienceDisplayUrl(channelName, url);
  }

  const copyInput = modal.querySelector('#qr-modal-copy-input') as HTMLInputElement | null;
  if (copyInput) {
    copyInput.value = url || getAudienceUrl(channelName);
  }

  const spinner = modal.querySelector('#qr-modal-spinner') as HTMLElement | null;
  const container = modal.querySelector('#qr-modal-svg-container') as HTMLElement | null;
  const target = modal.querySelector('#qr-modal-svg-target') as HTMLElement | null;

  if (spinner) spinner.classList.remove(CLASS_HIDDEN);
  if (container) {
    container.classList.add(CLASS_HIDDEN);
    container.replaceChildren();
  }

  QRCode.toString(url, {
    type: 'svg',
    margin: 2,
    color: {
      dark: '#000000',
      light: '#ffffff',
    },
  }).then((svgString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgEl = doc.documentElement;

    if (container) {
      container.replaceChildren(svgEl);
      if (spinner) spinner.classList.add(CLASS_HIDDEN);
      container.classList.remove(CLASS_HIDDEN);
    } else if (target) {
      target.replaceChildren(svgEl);
    }
  }).catch((err) => {
    console.error('Failed to generate modal QR code:', err);
    if (spinner) spinner.classList.add(CLASS_HIDDEN);
  });

  const closeModal = () => {
    modal.classList.add(CLASS_CLOSING);
    setTimeout(() => {
      modal.classList.add(CLASS_HIDDEN);
      modal.classList.remove(CLASS_CLOSING);
    }, 150);
  };

  modal.classList.remove(CLASS_HIDDEN);

  const closeBtn = modal.querySelector('.channel-qr-close-btn');
  if (closeBtn && !(closeBtn as any)._rtcBound) {
    (closeBtn as any)._rtcBound = true;
    closeBtn.addEventListener(EVENT_CLICK, closeModal);
  }

  if (!(modal as any)._rtcBound) {
    (modal as any)._rtcBound = true;
    modal.addEventListener(EVENT_CLICK, (e) => {
      if (e.target === modal) closeModal();
    });

    window.addEventListener(EVENT_KEYDOWN, (e: KeyboardEvent) => {
      if (e.key === KEY_ESCAPE && !modal.classList.contains(CLASS_HIDDEN)) {
        closeModal();
      }
    });

    const copyBtn = modal.querySelector('#qr-modal-copy-btn') as HTMLButtonElement | null;
    copyBtn?.addEventListener(EVENT_CLICK, () => {
      const input = modal.querySelector('#qr-modal-copy-input') as HTMLInputElement | null;
      const textToCopy = input?.value || url;
      if (copyBtn) copyWithFeedback(copyBtn, textToCopy);
    });

    copyInput?.addEventListener(EVENT_CLICK, () => {
      copyInput.select();
    });
  }
}

/**
 * Binds and updates an existing channel badge DOM element with dynamic data.
 */
export function setupChannelBadge(badgeEl: HTMLElement, options: ChannelBadgeOptions): void {
  const channelClean = cleanChannelName(options.channel);
  const fullUrl = options.url || getAudienceUrl(options.channel);
  const displayText = getAudienceDisplayUrl(options.channel, options.url);

  const textEl = badgeEl.querySelector('.channel-badge-url');
  if (textEl) {
    textEl.textContent = displayText;
  }

  const defaultLogo = badgeEl.querySelector('.channel-badge-default-logo') as HTMLElement | null;
  const customLogo = badgeEl.querySelector('.channel-badge-custom-logo') as HTMLImageElement | null;

  const rawSrc = customLogo?.getAttribute('src') || null;
  const isPlaceholder = !rawSrc || rawSrc.startsWith('data:image/svg+xml') || customLogo?.classList.contains(CLASS_HIDDEN);
  const effectiveLogoUrl = options.customLogoUrl !== undefined
    ? options.customLogoUrl
    : (isPlaceholder ? null : rawSrc);

  bindImage(customLogo, effectiveLogoUrl, { fallbackEl: defaultLogo });

  const qrBtn = badgeEl.querySelector('.channel-badge-qr') as HTMLButtonElement | null;
  if (qrBtn) {
    qrBtn.setAttribute('aria-label', `Show QR code for ${displayText}`);
    qrBtn.onclick = (e) => {
      e.stopPropagation();
      openQRCodeModal(fullUrl, channelClean);
    };
  }

  if (options.clickable !== false) {
    badgeEl.style.cursor = 'pointer';
    badgeEl.onclick = () => {
      openQRCodeModal(fullUrl, channelClean);
    };
  }
}

/**
 * Creates or clones a channel badge element and populates it.
 * Uses #channel-badge-template from HTML if available; otherwise constructs DOM cleanly.
 */
export function createChannelBadgeElement(options: ChannelBadgeOptions): HTMLElement {
  const template = document.getElementById('channel-badge-template') as HTMLTemplateElement | null;
  if (template) {
    const clone = template.content.cloneNode(true) as DocumentFragment;
    const badge = clone.querySelector('.channel-badge-pill') as HTMLElement;
    setupChannelBadge(badge, options);
    return badge;
  }

  throw new Error('#channel-badge-template missing');
}
